import { Badge, BlockStack, Button, IndexTable, Modal, Spinner, Text, Tooltip } from "@shopify/polaris"
import dayjs from "dayjs"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import OrdersAPI from "@/api/orders"
import { formatNumber } from "@/utils/analyticsUtils"

import { EXPORT_HISTORY_LAUNCH_DATE } from "./exportHistory"
import styles from "./ExportHistoryModal.module.scss"
import { ExportHistoryItem, ExportHistoryPage, ExportHistoryStatus } from "./types"

const PAGE_SIZE = 20
/** 有 Processing 记录时轮询最新一页的间隔，让状态及时变成 Completed / Failed */
const POLL_INTERVAL = 5_000

const statusTone = {
  [ExportHistoryStatus.Processing]: "info",
  [ExportHistoryStatus.Completed]: "success",
  [ExportHistoryStatus.Failed]: "critical",
} as const

const statusI18nKey = {
  [ExportHistoryStatus.Processing]: "orders:ExportHistory.Processing",
  [ExportHistoryStatus.Completed]: "orders:ExportHistory.Completed",
  [ExportHistoryStatus.Failed]: "orders:ExportHistory.Failed",
} as const

interface ExportHistoryModalProps {
  open: boolean
  onClose(): void
}

export function ExportHistoryModal({ open, onClose }: ExportHistoryModalProps) {
  const { t } = useTranslation(["orders", "common"])

  const [items, setItems] = useState<ExportHistoryItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loadingFirstPage, setLoadingFirstPage] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  /** 正在换取下载链接的行（按下标，id 每次请求可能不同） */
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  // 防止滚动触发重复请求
  const loadingMoreRef = useRef(false)
  // 关闭或重新加载后丢弃过期请求的结果
  const requestSeqRef = useRef(0)

  const applyFirstPage = (data: ExportHistoryPage) => {
    setItems(data.list)
    setNextCursor(data.has_more ? data.next_cursor : null)
  }

  const loadFirstPage = useCallback(() => {
    const seq = ++requestSeqRef.current

    setItems([])
    setNextCursor(null)
    setLoadingFirstPage(true)

    OrdersAPI.getExportHistory({ limit: PAGE_SIZE })
      .then(({ data: { data } }) => seq === requestSeqRef.current && applyFirstPage(data))
      .catch(() => void 0)
      .finally(() => seq === requestSeqRef.current && setLoadingFirstPage(false))
  }, [])

  // 打开时重新拉第一页
  useEffect(() => {
    if (open) {
      loadFirstPage()
    } else {
      requestSeqRef.current++
    }
  }, [open, loadFirstPage])

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMoreRef.current) {
      return
    }

    const seq = requestSeqRef.current

    loadingMoreRef.current = true
    setLoadingMore(true)

    OrdersAPI.getExportHistory({ cursor: nextCursor, limit: PAGE_SIZE })
      .then(({ data: { data } }) => {
        if (seq !== requestSeqRef.current) {
          return
        }
        setItems(current => [...current, ...data.list])
        setNextCursor(data.has_more ? data.next_cursor : null)
      })
      // 游标非法（422）：丢弃游标，从首页重新加载
      .catch(() => seq === requestSeqRef.current && loadFirstPage())
      .finally(() => {
        loadingMoreRef.current = false
        setLoadingMore(false)
      })
  }, [nextCursor, loadFirstPage])

  // 滚动到底部附近时加载下一页
  useEffect(() => {
    const root = scrollRef.current
    const target = sentinelRef.current

    if (!open || !nextCursor || !root || !target) {
      return
    }

    const io = new IntersectionObserver(
      entries => entries.some(entry => entry.isIntersecting) && loadMore(),
      { root, rootMargin: "100px" },
    )

    io.observe(target)

    return () => io.disconnect()
  }, [open, nextCursor, loadMore, items.length])

  // 有 Processing 的记录时轮询，及时更新状态。
  // id 是加密 token、每次请求都可能不同，无法按 id 合并，直接用最新首页替换列表
  const hasProcessing = items.some(item => item.status === ExportHistoryStatus.Processing)

  useEffect(() => {
    if (!open || !hasProcessing) {
      return
    }

    const timer = setInterval(() => {
      if (loadingMoreRef.current) {
        return
      }

      const seq = requestSeqRef.current

      OrdersAPI.getExportHistory({ limit: PAGE_SIZE })
        .then(({ data: { data } }) => seq === requestSeqRef.current && applyFirstPage(data))
        .catch(() => void 0)
    }, POLL_INTERVAL)

    return () => clearInterval(timer)
  }, [open, hasProcessing])

  const handleDownload = (item: ExportHistoryItem, index: number) => {
    setDownloadingIndex(index)

    OrdersAPI.getExportDownloadUrl(item.id)
      .then(({ data: { data } }) => {
        // 在当前页下载，不开新标签页；依赖下载链接返回 Content-Disposition: attachment，否则会跳转到文件内容
        const linkNode = document.createElement("a")

        linkNode.href = data.download_url
        linkNode.download = item.file_name
        linkNode.style.display = "none"
        document.body.appendChild(linkNode)
        linkNode.click()
        document.body.removeChild(linkNode)
      })
      .catch(() => {
        // 文件过期或 id 非法：baseRequest 已提示后端 msg，这里刷新列表
        loadFirstPage()
      })
      .finally(() => setDownloadingIndex(null))
  }

  const rowMarkup = items.map((item, index) => (
    <IndexTable.Row id={String(index)} key={`${index}-${item.id}`} position={index}>
      <IndexTable.Cell>
        <Tooltip content={item.file_name} preferredPosition="above">
          <div className="max-w-[160px]">
            <Text as="span" truncate>{item.file_name}</Text>
          </div>
        </Tooltip>
      </IndexTable.Cell>
      <IndexTable.Cell>{item.shipments === null ? "-" : formatNumber(item.shipments)}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={statusTone[item.status]}>{t(statusI18nKey[item.status])}</Badge>
      </IndexTable.Cell>
      {/* 后端已按店铺时区格式化，直接展示 */}
      <IndexTable.Cell>{item.export_date}</IndexTable.Cell>
      <IndexTable.Cell>
        {item.downloadable
          ? (
            <Button
              variant="plain"
              loading={downloadingIndex === index}
              disabled={downloadingIndex !== null && downloadingIndex !== index}
              onClick={() => handleDownload(item, index)}
            >
              {t("orders:ExportHistory.Download")}
            </Button>
          )
          : "-"}
      </IndexTable.Cell>
    </IndexTable.Row>
  ))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("orders:ExportHistory.title")}
      secondaryActions={[{ content: t("common:Close"), onAction: onClose }]}
      sectioned
    >
      <BlockStack gap="300">
        <Text as="p">
          {t("orders:ExportHistory.desc", { date: dayjs(EXPORT_HISTORY_LAUNCH_DATE).format("MMM D, YYYY") })}
        </Text>

        <div ref={scrollRef} className={styles.TableScroll}>
          <IndexTable
            selectable={false}
            loading={loadingFirstPage}
            itemCount={items.length}
            emptyState={loadingFirstPage ? <div className="h-16" /> : (
              <div className="py-6 text-center">
                <Text as="p" tone="subdued">{t("orders:ExportHistory.Empty")}</Text>
              </div>
            )}
            headings={[
              { title: t("orders:ExportHistory.FileName") },
              { title: t("orders:ExportHistory.Shipments") },
              { title: t("orders:ExportHistory.Status") },
              { title: t("orders:ExportHistory.ExportDate") },
              { title: "", id: "download" },
            ]}
          >
            {rowMarkup}
          </IndexTable>

          <div ref={sentinelRef} />

          {loadingMore && (
            <div className="flex justify-center py-3">
              <Spinner size="small" />
            </div>
          )}
        </div>
      </BlockStack>
    </Modal>
  )
}
